import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Loader2, ArrowRight, Copy, Check, ArrowLeft } from "lucide-react";

const DEMO_EMAIL = "admin@channelops.com";
const DEMO_PASSWORD = "ops12345";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCreds = async () => {
    try {
      await navigator.clipboard.writeText(`${DEMO_EMAIL} / ${DEMO_PASSWORD}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  const fillDemo = () => { setEmail(DEMO_EMAIL); setPassword(DEMO_PASSWORD); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Creator");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch relative" style={{ background: "radial-gradient(1200px 600px at 50% -5%, #16130B 0%, #0C0B0C 42%, #0A0A0B 100%)" }}>
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="flex items-center gap-2 relative z-10">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center text-primary-foreground font-display font-extrabold">C</div>
          <span className="font-extrabold text-xl tracking-tight no-underline" style={{ fontFamily: "'Manrope', sans-serif" }}>Channel<span className="text-primary">HQ</span></span>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="font-mono text-xs text-primary tracking-[0.25em] uppercase mb-4">Solo creator · multi-channel</p>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            One command center for every channel you run.
          </h1>
          <p className="text-muted-foreground mt-5 leading-relaxed">
            Track every video across NightVault, KarmaVault and beyond — from idea to published — without losing the thread in scattered notes.
          </p>
        </div>
        <div className="relative z-10 font-mono text-xs text-muted-foreground tracking-wider">
          IDEA → SCRIPT → VOICEOVER → EDITING → THUMBNAIL → SCHEDULED → PUBLISHED
        </div>
      </div>

      {/* Right form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <h2 className="font-display font-extrabold text-3xl tracking-tight mb-1">
            {mode === "login" ? "Welcome back" : "Create workspace"}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {mode === "login" ? "Sign in to your studio command center." : "Set up your creator workspace."}
          </p>

          {mode === "login" && (
            <div data-testid="demo-credentials" className="mb-6 rounded-md bg-primary/10 border border-primary/30 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">Demo access</span>
                <button onClick={copyCreds} data-testid="copy-credentials-button"
                  className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <p className="font-mono text-sm text-foreground break-all">{DEMO_EMAIL}</p>
              <p className="font-mono text-sm text-foreground">{DEMO_PASSWORD}</p>
              <button onClick={fillDemo} data-testid="autofill-demo-button"
                className="mt-3 w-full text-xs font-bold bg-primary text-primary-foreground rounded-md py-2 hover:bg-[#C29035] transition-colors duration-200">
                Fill demo credentials
              </button>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Name" value={name} onChange={setName} placeholder="Your name" testid="name-input" />
            )}
            <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@studio.com" testid="email-input" required />
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" testid="password-input" required />

            {error && (
              <p data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-4 py-3 hover:bg-[#C29035] transition-colors duration-200 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>{mode === "login" ? "Sign in" : "Create workspace"} <ArrowRight size={18} /></>}
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            {mode === "login" ? "No workspace yet? " : "Already set up? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-medium hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1.5 tracking-wide">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200"
      />
    </div>
  );
}
